
/**
 * A color utility library.  Inspired by tinycolor, but modified to work for us
 */
export class Color {

    private _originalInput: any;
    private _r: number;
    private _g: number;
    private _b: number;
    private _a: number;
    private _roundA: number;
    private _ok: boolean;

    constructor(color: any) {
        let rgb = Color.inputToRGB(color);
        this._originalInput = color,
        this._r = rgb.r,
        this._g = rgb.g,
        this._b = rgb.b,
        this._a = rgb.a,
        this._roundA = Math.round(100 * this._a) / 100;

        // Don't let the range of [0,255] come back in [0,1].
        // Potentially lose a little bit of precision here, but will fix issues where
        // .5 gets interpreted as half of the total, instead of half of 1
        // If it was supposed to be 128, this was already taken care of by `inputToRgb`
        if (this._r < 1) { this._r = Math.round(this._r); }
        if (this._g < 1) { this._g = Math.round(this._g); }
        if (this._b < 1) { this._b = Math.round(this._b); }

        this._ok = rgb.ok;
    }

    // Big List of Colors
    // ------------------
    // <http://www.w3.org/TR/css3-color/#svg-color>
    public static names = {
        aliceblue: "f0f8ff",
        antiquewhite: "faebd7",
        aqua: "0ff",
        aquamarine: "7fffd4",
        azure: "f0ffff",
        beige: "f5f5dc",
        bisque: "ffe4c4",
        black: "000",
        blanchedalmond: "ffebcd",
        blue: "00f",
        blueviolet: "8a2be2",
        brown: "a52a2a",
        burlywood: "deb887",
        burntsienna: "ea7e5d",
        cadetblue: "5f9ea0",
        chartreuse: "7fff00",
        chocolate: "d2691e",
        coral: "ff7f50",
        cornflowerblue: "6495ed",
        cornsilk: "fff8dc",
        crimson: "dc143c",
        cyan: "0ff",
        darkblue: "00008b",
        darkcyan: "008b8b",
        darkgoldenrod: "b8860b",
        darkgray: "a9a9a9",
        darkgreen: "006400",
        darkgrey: "a9a9a9",
        darkkhaki: "bdb76b",
        darkmagenta: "8b008b",
        darkolivegreen: "556b2f",
        darkorange: "ff8c00",
        darkorchid: "9932cc",
        darkred: "8b0000",
        darksalmon: "e9967a",
        darkseagreen: "8fbc8f",
        darkslateblue: "483d8b",
        darkslategray: "2f4f4f",
        darkslategrey: "2f4f4f",
        darkturquoise: "00ced1",
        darkviolet: "9400d3",
        deeppink: "ff1493",
        deepskyblue: "00bfff",
        dimgray: "696969",
        dimgrey: "696969",
        dodgerblue: "1e90ff",
        firebrick: "b22222",
        floralwhite: "fffaf0",
        forestgreen: "228b22",
        fuchsia: "f0f",
        gainsboro: "dcdcdc",
        ghostwhite: "f8f8ff",
        gold: "ffd700",
        goldenrod: "daa520",
        gray: "808080",
        green: "008000",
        greenyellow: "adff2f",
        grey: "808080",
        honeydew: "f0fff0",
        hotpink: "ff69b4",
        indianred: "cd5c5c",
        indigo: "4b0082",
        ivory: "fffff0",
        khaki: "f0e68c",
        lavender: "e6e6fa",
        lavenderblush: "fff0f5",
        lawngreen: "7cfc00",
        lemonchiffon: "fffacd",
        lightblue: "add8e6",
        lightcoral: "f08080",
        lightcyan: "e0ffff",
        lightgoldenrodyellow: "fafad2",
        lightgray: "d3d3d3",
        lightgreen: "90ee90",
        lightgrey: "d3d3d3",
        lightpink: "ffb6c1",
        lightsalmon: "ffa07a",
        lightseagreen: "20b2aa",
        lightskyblue: "87cefa",
        lightslategray: "789",
        lightslategrey: "789",
        lightsteelblue: "b0c4de",
        lightyellow: "ffffe0",
        lime: "0f0",
        limegreen: "32cd32",
        linen: "faf0e6",
        magenta: "f0f",
        maroon: "800000",
        mediumaquamarine: "66cdaa",
        mediumblue: "0000cd",
        mediumorchid: "ba55d3",
        mediumpurple: "9370db",
        mediumseagreen: "3cb371",
        mediumslateblue: "7b68ee",
        mediumspringgreen: "00fa9a",
        mediumturquoise: "48d1cc",
        mediumvioletred: "c71585",
        midnightblue: "191970",
        mintcream: "f5fffa",
        mistyrose: "ffe4e1",
        moccasin: "ffe4b5",
        navajowhite: "ffdead",
        navy: "000080",
        oldlace: "fdf5e6",
        olive: "808000",
        olivedrab: "6b8e23",
        orange: "ffa500",
        orangered: "ff4500",
        orchid: "da70d6",
        palegoldenrod: "eee8aa",
        palegreen: "98fb98",
        paleturquoise: "afeeee",
        palevioletred: "db7093",
        papayawhip: "ffefd5",
        peachpuff: "ffdab9",
        peru: "cd853f",
        pink: "ffc0cb",
        plum: "dda0dd",
        powderblue: "b0e0e6",
        purple: "800080",
        rebeccapurple: "663399",
        red: "f00",
        rosybrown: "bc8f8f",
        royalblue: "4169e1",
        saddlebrown: "8b4513",
        salmon: "fa8072",
        sandybrown: "f4a460",
        seagreen: "2e8b57",
        seashell: "fff5ee",
        sienna: "a0522d",
        silver: "c0c0c0",
        skyblue: "87ceeb",
        slateblue: "6a5acd",
        slategray: "708090",
        slategrey: "708090",
        snow: "fffafa",
        springgreen: "00ff7f",
        steelblue: "4682b4",
        tan: "d2b48c",
        teal: "008080",
        thistle: "d8bfd8",
        tomato: "ff6347",
        turquoise: "40e0d0",
        violet: "ee82ee",
        wheat: "f5deb3",
        white: "fff",
        whitesmoke: "f5f5f5",
        yellow: "ff0",
        yellowgreen: "9acd32"
    };

    // Make it easy to access colors via `hexNames[hex]`
    public static hexNames = Color.flip(Color.names);

    public isDark(): boolean {
        return this.getBrightness() < 128;
    }
    public isLight(): boolean {
        return !this.isDark();
    }
    public isValid(): boolean {
        return this._ok;
    }
    public getOriginalInput() {
        return this._originalInput;
    }
    public getAlpha() {
        return this._a;
    }
    public getBrightness() {
        // http://www.w3.org/TR/AERT#color-contrast
        let rgb = this.toRgb();
        return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    }
    public getLuminance() {
        // http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
        let rgb = this.toRgb();
        let R;
        let G;
        let B;
        let rsRGB = rgb.r / 255;
        let gsRGB = rgb.g / 255;
        let bsRGB = rgb.b / 255;

        if (rsRGB <= 0.03928) { R = rsRGB / 12.92; } else { R = Math.pow(((rsRGB + 0.055) / 1.055), 2.4); }
        if (gsRGB <= 0.03928) { G = gsRGB / 12.92; } else { G = Math.pow(((gsRGB + 0.055) / 1.055), 2.4); }
        if (bsRGB <= 0.03928) { B = bsRGB / 12.92; } else { B = Math.pow(((bsRGB + 0.055) / 1.055), 2.4); }
        return (0.2126 * R) + (0.7152 * G) + (0.0722 * B);
    }
    public setAlpha(value) {
        this._a = Color.boundAlpha(value);
        this._roundA = Math.round(100 * this._a) / 100;
        return this;
    }
    public toHsv() {
        let hsv = Color.rgbToHsv(this._r, this._g, this._b);
        return { h: hsv.h * 360, s: hsv.s, v: hsv.v, a: this._a };
    }
    public toHsvString() {
        let hsv = Color.rgbToHsv(this._r, this._g, this._b);
        let h = Math.round(hsv.h * 360);
        let s = Math.round(hsv.s * 100);
        let v = Math.round(hsv.v * 100);
        return (this._a == 1) ?
            "hsv(" + h + ", " + s + "%, " + v + "%)" :
            "hsva(" + h + ", " + s + "%, " + v + "%, " + this._roundA + ")";
    }
    public toHsl() {
        let hsl = Color.rgbToHsl(this._r, this._g, this._b);
        return { h: hsl.h * 360, s: hsl.s, l: hsl.l, a: this._a };
    }
    public toHslString() {
        let hsl = Color.rgbToHsl(this._r, this._g, this._b);
        let h = Math.round(hsl.h * 360);
        let s = Math.round(hsl.s * 100);
        let l = Math.round(hsl.l * 100);
        return (this._a == 1) ?
            "hsl(" + h + ", " + s + "%, " + l + "%)" :
            "hsla(" + h + ", " + s + "%, " + l + "%, " + this._roundA + ")";
    }
    public toHex(allow3Char: boolean = true) {
        return Color.rgbToHex(this._r, this._g, this._b, allow3Char);
    }
    public toHexString(allow3Char: boolean = true) {
        return "#" + this.toHex(allow3Char);
    }
    public toHex8() {
        return Color.rgbaToHex(this._r, this._g, this._b, this._a);
    }
    public toHex8String() {
        return "#" + this.toHex8();
    }
    public toRgb() {
        return { r: Math.round(this._r), g: Math.round(this._g), b: Math.round(this._b), a: this._a };
    }
    public toRgbString() {
        return (this._a == 1) ?
            "rgb(" + Math.round(this._r) + ", " +
            Math.round(this._g) + ", " +
            Math.round(this._b) + ")" :
            "rgba(" + Math.round(this._r) + ", " +
            Math.round(this._g) + ", " +
            Math.round(this._b) + ", " +
            this._roundA + ")";
    }
    public toPercentageRgb() {
        return {
            r: Math.round(Color.bound01(this._r, 255) * 100) + "%",
            g: Math.round(Color.bound01(this._g, 255) * 100) + "%",
            b: Math.round(Color.bound01(this._b, 255) * 100) + "%", a: this._a
        };
    }
    public toPercentageRgbString() {
        return (this._a == 1) ?
            "rgb(" +
            Math.round(Color.bound01(this._r, 255) * 100) + "%, " +
            Math.round(Color.bound01(this._g, 255) * 100) + "%, " +
            Math.round(Color.bound01(this._b, 255) * 100) + "%)" :
            "rgba(" +
            Math.round(Color.bound01(this._r, 255) * 100) + "%, " +
            Math.round(Color.bound01(this._g, 255) * 100) + "%, " +
            Math.round(Color.bound01(this._b, 255) * 100) + "%, " +
            this._roundA + ")";
    }
    public toName(): string {
        if (this._a === 0) {
            return "transparent";
        }

        if (this._a < 1) {
            return undefined;
        }

        return Color.hexNames[Color.rgbToHex(this._r, this._g, this._b, true)] || undefined;
    }
    public toString(format) {
        let formatSet = !!format;

        let formattedString: string;
        let hasAlpha = this._a < 1 && this._a >= 0;
        let needsAlphaFormat = !formatSet && hasAlpha &&
            (format === "hex" || format === "hex6" || format === "hex3" || format === "name");

        if (needsAlphaFormat) {
            // Special case for "transparent", all other non-alpha formats
            // will return rgba when there is transparency.
            if (format === "name" && this._a === 0) {
                return this.toName();
            }
            return this.toRgbString();
        }
        if (format === "rgb") {
            formattedString = this.toRgbString();
        }
        if (format === "prgb") {
            formattedString = this.toPercentageRgbString();
        }
        if (format === "hex" || format === "hex6") {
            formattedString = this.toHexString();
        }
        if (format === "hex3") {
            formattedString = this.toHexString(true);
        }
        if (format === "hex8") {
            formattedString = this.toHex8String();
        }
        if (format === "name") {
            formattedString = this.toName();
        }
        if (format === "hsl") {
            formattedString = this.toHslString();
        }
        if (format === "hsv") {
            formattedString = this.toHsvString();
        }

        return formattedString || this.toHexString();
    }

    protected _applyModification(fn, args) {
        let color = fn.apply(null, [this].concat([].slice.call(args)));
        this._r = color._r;
        this._g = color._g;
        this._b = color._b;
        this.setAlpha(color._a);
        return this;
    }
    public lighten(amount: number) {
        return this._applyModification(Color.lighten, arguments);
    }
    public brighten(amount: number) {
        return this._applyModification(Color.brighten, arguments);
    }
    public darken(amount: number) {
        return this._applyModification(Color.darken, arguments);
    }
    public desaturate(amount: number) {
        return this._applyModification(Color.desaturate, arguments);
    }
    public saturate(amount: number) {
        return this._applyModification(Color.saturate, arguments);
    }
    public greyscale() {
        return this._applyModification(Color.greyscale, arguments);
    }
    public spin(amount: number) {
        return this._applyModification(Color.spin, arguments);
    }

    private _applyCombination(fn, args) {
        return fn.apply(null, [this].concat([].slice.call(args)));
    }
    public analogous(results: number, slices: number) {
        return this._applyCombination(Color.analogous, arguments);
    }
    public complement() {
        return this._applyCombination(Color.complement, arguments);
    }
    public monochromatic(results: number) {
        return this._applyCombination(Color.monochromatic, arguments);
    }
    public splitcomplement() {
        return this._applyCombination(Color.splitcomplement, arguments);
    }
    public triad() {
        return this._applyCombination(Color.triad, arguments);
    }
    public tetrad() {
        return this._applyCombination(Color.tetrad, arguments);
    }

    // If input is an object, force 1 into "1.0" to handle ratios properly
    // String input requires "1.0" as input, so 1 will be treated as 1
    public static fromRatio(color) {
        if (typeof color == "object") {
            let newColor = {};
            for (let i in color) {
                if (color.hasOwnProperty(i)) {
                    if (i === "a") {
                        newColor[i] = color[i];
                    } else {
                        newColor[i] = Color.convertToPercentage(color[i]);
                    }
                }
            }
            color = newColor;
        }

        return new Color(color);
    }

    // Given a string or object, convert that input to RGB
    // Possible string inputs:
    //
    //     "red"
    //     "#f00" or "f00"
    //     "#ff0000" or "ff0000"
    //     "#ff000000" or "ff000000"
    //     "rgb 255 0 0" or "rgb (255, 0, 0)"
    //     "rgb 1.0 0 0" or "rgb (1, 0, 0)"
    //     "rgba (255, 0, 0, 1)" or "rgba 255, 0, 0, 1"
    //     "rgba (1.0, 0, 0, 1)" or "rgba 1.0, 0, 0, 1"
    //     "hsl(0, 100%, 50%)" or "hsl 0 100% 50%"
    //     "hsla(0, 100%, 50%, 1)" or "hsla 0 100% 50%, 1"
    //     "hsv(0, 100%, 100%)" or "hsv 0 100% 100%"
    //
    public static inputToRGB(color) {

        let rgb = { r: 0, g: 0, b: 0 };
        let a = 1;
        let ok = false;
        let format: string;

        if (color instanceof Color) {
            rgb.r = color._r;
            rgb.b = color._b;
            rgb.g = color._g;
            a = color._a;
        }
        if (typeof color == "string") {
            color = Color.stringInputToObject(color);
        }

        if (typeof color == "object" && color != null) {
            if (color.hasOwnProperty("r") && color.hasOwnProperty("g") && color.hasOwnProperty("b")) {
                rgb = Color.rgbToRgb(color.r, color.g, color.b);
                ok = true;
                format = String(color.r).substr(-1) === "%" ? "prgb" : "rgb";
            } else if (color.hasOwnProperty("h") && color.hasOwnProperty("s") && color.hasOwnProperty("v")) {
                color.s = Color.convertToPercentage(color.s);
                color.v = Color.convertToPercentage(color.v);
                rgb = Color.hsvToRgb(color.h, color.s, color.v);
                ok = true;
                format = "hsv";
            } else if (color.hasOwnProperty("h") && color.hasOwnProperty("s") && color.hasOwnProperty("l")) {
                color.s = Color.convertToPercentage(color.s);
                color.l = Color.convertToPercentage(color.l);
                rgb = Color.hslToRgb(color.h, color.s, color.l);
                ok = true;
                format = "hsl";
            }

            if (color.hasOwnProperty("a")) {
                a = color.a;
            }
        }

        a = Color.boundAlpha(a);

        return {
            ok,
            format: color?.format || format,
            r: Math.min(255, Math.max(rgb.r, 0)),
            g: Math.min(255, Math.max(rgb.g, 0)),
            b: Math.min(255, Math.max(rgb.b, 0)),
            a
        };
    }

    // Conversion Functions
    // --------------------

    // `rgbToHsl`, `rgbToHsv`, `hslToRgb`, `hsvToRgb` modified from:
    // <http://mjijackson.com/2008/02/rgb-to-hsl-and-rgb-to-hsv-color-model-conversion-algorithms-in-javascript>

    // `rgbToRgb`
    // Handle bounds / percentage checking to conform to CSS color spec
    // <http://www.w3.org/TR/css3-color/>
    // *Assumes:* r, g, b in [0, 255] or [0, 1]
    // *Returns:* { r, g, b } in [0, 255]
    public static rgbToRgb(r, g, b) {
        return {
            r: Color.bound01(r, 255) * 255,
            g: Color.bound01(g, 255) * 255,
            b: Color.bound01(b, 255) * 255
        };
    }

    // `rgbToHsl`
    // Converts an RGB color value to HSL.
    // *Assumes:* r, g, and b are contained in [0, 255] or [0, 1]
    // *Returns:* { h, s, l } in [0,1]
    public static rgbToHsl(r, g, b) {

        r = Color.bound01(r, 255);
        g = Color.bound01(g, 255);
        b = Color.bound01(b, 255);

        let max = Math.max(r, g, b);
        let min = Math.min(r, g, b);
        let h;
        let s;
        let l = (max + min) / 2;

        if (max == min) {
            h = s = 0; // achromatic
        } else {
            let d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }

            h /= 6;
        }

        return { h, s, l };
    }

    // `hslToRgb`
    // Converts an HSL color value to RGB.
    // *Assumes:* h is contained in [0, 1] or [0, 360] and s and l are contained [0, 1] or [0, 100]
    // *Returns:* { r, g, b } in the set [0, 255]
    public static hslToRgb(h, s, l) {
        let r;
        let g;
        let b;

        h = Color.bound01(h, 360);
        s = Color.bound01(s, 100);
        l = Color.bound01(l, 100);

        function hue2rgb(p, q, t) {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        }

        if (s === 0) {
            r = g = b = l; // achromatic
        } else {
            let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            let p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        return { r: r * 255, g: g * 255, b: b * 255 };
    }

    // `rgbToHsv`
    // Converts an RGB color value to HSV
    // *Assumes:* r, g, and b are contained in the set [0, 255] or [0, 1]
    // *Returns:* { h, s, v } in [0,1]
    public static rgbToHsv(r, g, b) {

        r = Color.bound01(r, 255);
        g = Color.bound01(g, 255);
        b = Color.bound01(b, 255);

        let max = Math.max(r, g, b);
        let min = Math.min(r, g, b);
        let h;
        let s;
        let v = max;

        let d = max - min;
        s = max === 0 ? 0 : d / max;

        if (max == min) {
            h = 0; // achromatic
        } else {
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }
        return { h, s, v };
    }

    // `hsvToRgb`
    // Converts an HSV color value to RGB.
    // *Assumes:* h is contained in [0, 1] or [0, 360] and s and v are contained in [0, 1] or [0, 100]
    // *Returns:* { r, g, b } in the set [0, 255]
    public static hsvToRgb(h, s, v) {

        h = Color.bound01(h, 360) * 6;
        s = Color.bound01(s, 100);
        v = Color.bound01(v, 100);

        let i = Math.floor(h);
        let f = h - i;
        let p = v * (1 - s);
        let q = v * (1 - f * s);
        let t = v * (1 - (1 - f) * s);
        let mod = i % 6;
        let r = [v, q, p, p, t, v][mod];
        let g = [t, v, v, q, p, p][mod];
        let b = [p, p, t, v, v, q][mod];

        return { r: r * 255, g: g * 255, b: b * 255 };
    }

    // `rgbToHex`
    // Converts an RGB color to hex
    // Assumes r, g, and b are contained in the set [0, 255]
    // Returns a 3 or 6 character hex
    public static rgbToHex(r, g, b, allow3Char) {

        let hex = [
            Color.pad2(Math.round(r).toString(16)),
            Color.pad2(Math.round(g).toString(16)),
            Color.pad2(Math.round(b).toString(16))
        ];

        // Return a 3 character hex if possible
        if (allow3Char && hex[0].charAt(0) == hex[0].charAt(1) && hex[1].charAt(0) == hex[1].charAt(1) &&
            hex[2].charAt(0) == hex[2].charAt(1)) {
            return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0);
        }

        return hex.join("");
    }
    // `rgbaToHex`
    // Converts an RGBA color plus alpha transparency to hex
    // Assumes r, g, b and a are contained in the set [0, 255]
    // Returns an 8 character hex
    public static rgbaToHex(r, g, b, a) {

        let hex = [
            Color.pad2(Color.convertDecimalToHex(a)),
            Color.pad2(Math.round(r).toString(16)),
            Color.pad2(Math.round(g).toString(16)),
            Color.pad2(Math.round(b).toString(16))
        ];

        return hex.join("");
    }

    // `equals`
    // Can be called with any tinycolor input
    public static equals(color1, color2) {
        if (!color1 || !color2) { return false; }
        return new Color(color1).toRgbString() == new Color(color2).toRgbString();
    }
    public static random() {
        return Color.fromRatio({
            r: Math.random(),
            g: Math.random(),
            b: Math.random()
        });
    }

    // Modification Functions
    // ----------------------
    // Thanks to less.js for some of the basics here
    // <https://github.com/cloudhead/less.js/blob/master/lib/less/functions.js>

    public static desaturate(color, amount: number): Color {
        amount = (amount === 0) ? 0 : (amount || 10);
        let hsl = new Color(color).toHsl();
        hsl.s -= amount / 100;
        hsl.s = Color.clamp01(hsl.s);
        return new Color(hsl);
    }

    public static saturate(color, amount: number): Color {
        amount = (amount === 0) ? 0 : (amount || 10);
        let hsl = new Color(color).toHsl();
        hsl.s += amount / 100;
        hsl.s = Color.clamp01(hsl.s);
        return new Color(hsl);
    }

    public static greyscale(color): Color {
        return Color.desaturate(color, 100);
    }

    public static lighten(color, amount: number): Color {
        amount = (amount === 0) ? 0 : (amount || 10);
        let hsl = new Color(color).toHsl();
        hsl.l += amount / 100;
        hsl.l = Color.clamp01(hsl.l);
        return new Color(hsl);
    }

    public static brighten(color, amount: number): Color {
        amount = (amount === 0) ? 0 : (amount || 10);
        let rgb = new Color(color).toRgb();
        rgb.r = Math.max(0, Math.min(255, rgb.r - Math.round(255 * -(amount / 100))));
        rgb.g = Math.max(0, Math.min(255, rgb.g - Math.round(255 * -(amount / 100))));
        rgb.b = Math.max(0, Math.min(255, rgb.b - Math.round(255 * -(amount / 100))));
        return new Color(rgb);
    }

    public static darken(color, amount: number): Color {
        amount = (amount === 0) ? 0 : (amount || 10);
        let hsl = new Color(color).toHsl();
        hsl.l -= amount / 100;
        hsl.l = Color.clamp01(hsl.l);
        return new Color(hsl);
    }

    // Spin takes a positive or negative amount within [-360, 360] indicating the change of hue.
    // Values outside of this range will be wrapped into this range.
    public static spin(color, amount): Color {
        let hsl = new Color(color).toHsl();
        let hue = (Math.round(hsl.h) + amount) % 360;
        hsl.h = hue < 0 ? 360 + hue : hue;
        return new Color(hsl);
    }

    // Combination Functions
    // ---------------------
    // Thanks to jQuery xColor for some of the ideas behind these
    // <https://github.com/infusion/jQuery-xcolor/blob/master/jquery.xcolor.js>
    public static complement(color): Color {
        let hsl = new Color(color).toHsl();
        hsl.h = (hsl.h + 180) % 360;
        return new Color(hsl);
    }

    public static triad(color): Color[] {
        let hsl = new Color(color).toHsl();
        let h = hsl.h;
        return [
            new Color(color),
            new Color({ h: (h + 120) % 360, s: hsl.s, l: hsl.l }),
            new Color({ h: (h + 240) % 360, s: hsl.s, l: hsl.l })
        ];
    }

    public static tetrad(color): Color[] {
        let hsl = new Color(color).toHsl();
        let h = hsl.h;
        return [
            new Color(color),
            new Color({ h: (h + 90) % 360, s: hsl.s, l: hsl.l }),
            new Color({ h: (h + 180) % 360, s: hsl.s, l: hsl.l }),
            new Color({ h: (h + 270) % 360, s: hsl.s, l: hsl.l })
        ];
    }

    public static splitcomplement(color): Color[] {
        let hsl = new Color(color).toHsl();
        let h = hsl.h;
        return [
            new Color(color),
            new Color({ h: (h + 72) % 360, s: hsl.s, l: hsl.l }),
            new Color({ h: (h + 216) % 360, s: hsl.s, l: hsl.l })
        ];
    }

    public static analogous(color, results: number, slices: number): Color[] {
        results = results || 6;
        slices = slices || 30;

        let hsl = new Color(color).toHsl();
        let part = 360 / slices;
        let ret = [new Color(color)];

        // tslint:disable-next-line:no-bitwise
        for (hsl.h = ((hsl.h - (part * results >> 1)) + 720) % 360; --results;) {
            hsl.h = (hsl.h + part) % 360;
            ret.push(new Color(hsl));
        }
        return ret;
    }

    public static monochromatic(color, results: number): Color[] {
        results = results || 6;
        let hsv = new Color(color).toHsv();
        let h = hsv.h;
        let s = hsv.s;
        let v = hsv.v;
        let ret = [];
        let modification = 1 / results;

        while (results--) {
            ret.push(new Color({ h, s, v }));
            v = (v + modification) % 1;
        }

        return ret;
    }

    // Utility Functions
    // ---------------------
    public static mix(color1, color2, amount: number): Color {
        amount = (amount === 0) ? 0 : (amount || 50);

        let rgb1 = new Color(color1).toRgb();
        let rgb2 = new Color(color2).toRgb();

        let p = amount / 100;
        let w = p * 2 - 1;
        let a = rgb2.a - rgb1.a;

        let w1;

        if (w * a == -1) {
            w1 = w;
        } else {
            w1 = (w + a) / (1 + w * a);
        }

        w1 = (w1 + 1) / 2;

        let w2 = 1 - w1;

        let rgba = {
            r: rgb2.r * w1 + rgb1.r * w2,
            g: rgb2.g * w1 + rgb1.g * w2,
            b: rgb2.b * w1 + rgb1.b * w2,
            a: rgb2.a * p + rgb1.a * (1 - p)
        };

        return new Color(rgba);
    }

    // Readability Functions
    // ---------------------
    // <http://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef (WCAG Version 2)

    // `contrast`
    // Analyze the 2 colors and returns the color contrast defined by (WCAG Version 2)
    public static readability(color1, color2): number {
        let c1 = new Color(color1);
        let c2 = new Color(color2);
        return (Math.max(c1.getLuminance(), c2.getLuminance()) + 0.05) /
            (Math.min(c1.getLuminance(), c2.getLuminance()) + 0.05);
    }

    // `isReadable`
    // Ensure that foreground and background color combinations meet WCAG2 guidelines.
    // The third argument is an optional Object.
    //      the 'level' property states 'AA' or 'AAA' - if missing or invalid, it defaults to 'AA';
    //      the 'size' property states 'large' or 'small' - if missing or invalid, it defaults to 'small'.
    // If the entire object is absent, isReadable defaults to {level:"AA",size:"small"}.

    // *Example*
    //    tinycolor.isReadable("#000", "#111") => false
    //    tinycolor.isReadable("#000", "#111",{level:"AA",size:"large"}) => false

    public static isReadable(color1, color2, wcag2): boolean {
        let readability = Color.readability(color1, color2);
        let wcag2Parms;
        let out;

        out = false;

        wcag2Parms = Color.validateWCAG2Parms(wcag2);
        switch (wcag2Parms.level + wcag2Parms.size) {
            case "AAsmall":
            case "AAAlarge":
                out = readability >= 4.5;
                break;
            case "AAlarge":
                out = readability >= 3;
                break;
            case "AAAsmall":
                out = readability >= 7;
                break;
        }
        return out;

    }

    /**
     * Given a base color and a list of possible foreground or background
     * colors for that base, returns the most readable color.
     * Optionally returns Black or White if the most readable color is unreadable.
     * @param baseColor 
     * @param colorList 
     * @param args 
     */
    public static mostReadable(baseColor, colorList: any[], args): Color {
        let bestColor = null;
        let bestScore = 0;
        let readability;
        let includeFallbackColors;
        let level;
        let size;

        args = args || {};
        includeFallbackColors = args.includeFallbackColors;
        level = args.level;
        size = args.size;

        for (let c of colorList) {
            readability = Color.readability(baseColor, c);
            if (readability > bestScore) {
                bestScore = readability;
                bestColor = new Color(c);
            }
        }

        if (Color.isReadable(baseColor, bestColor, { level, size }) || !includeFallbackColors) {
            return bestColor;
        } else {
            args.includeFallbackColors = false;
            return Color.mostReadable(baseColor, ["#fff", "#000"], args);
        }
    }

    // Utilities
    // ---------

    // `{ 'name1': 'val1' }` becomes `{ 'val1': 'name1' }`
    protected static flip(o) {
        let flipped = {};
        for (let i in o) {
            if (o.hasOwnProperty(i)) {
                flipped[o[i]] = i;
            }
        }
        return flipped;
    }

    // Return a valid alpha value [0,1] with all invalid values being set to 1
    protected static boundAlpha(a) {
        a = parseFloat(a);

        if (isNaN(a) || a < 0 || a > 1) {
            a = 1;
        }

        return a;
    }

    // Take input from [0, n] and return it as [0, 1]
    protected static bound01(n, max): number {
        if (Color.isOnePointZero(n)) { n = "100%"; }

        let processPercent = Color.isPercentage(n);
        n = Math.min(max, Math.max(0, parseFloat(n)));

        // Automatically convert percentage into number
        if (processPercent) {
            n = parseInt((n * max).toString(), 10) / 100;
        }

        // Handle floating point rounding errors
        if ((Math.abs(n - max) < 0.000001)) {
            return 1;
        }

        // Convert into [0, 1] range if it isn't already
        return (n % max) / parseFloat(max);
    }

    // Force a number between 0 and 1
    protected static clamp01(val) {
        return Math.min(1, Math.max(0, val));
    }

    // Parse a base-16 hex value into a base-10 integer
    protected static parseIntFromHex(val) {
        return parseInt(val, 16);
    }

    // Need to handle 1.0 as 100%, since once it is a number, there is no difference between it and 1
    // <http://stackoverflow.com/questions/7422072/javascript-how-to-detect-number-as-a-decimal-including-1-0>
    protected static isOnePointZero(n) {
        return typeof n == "string" && n.indexOf(".") != -1 && parseFloat(n) === 1;
    }

    // Check to see if string passed in is a percentage
    protected static isPercentage(n) {
        return typeof n === "string" && n.indexOf("%") != -1;
    }

    // Force a hex value to have 2 characters
    protected static pad2(c) {
        return c.length == 1 ? "0" + c : "" + c;
    }

    // Replace a decimal with it's percentage value
    protected static convertToPercentage(n) {
        if (n <= 1) {
            n = (n * 100) + "%";
        }

        return n;
    }

    // Converts a decimal to a hex value
    protected static convertDecimalToHex(d) {
        return Math.round(parseFloat(d) * 255).toString(16);
    }
    // Converts a hex value to a decimal
    protected static convertHexToDecimal(h) {
        return (Color.parseIntFromHex(h) / 255);
    }

    protected static matchers = (() => {

        // <http://www.w3.org/TR/css3-values/#integers>
        let CSS_INTEGER = "[-\\+]?\\d+%?";

        // <http://www.w3.org/TR/css3-values/#number-value>
        let CSS_NUMBER = "[-\\+]?\\d*\\.\\d+%?";

        // Allow positive/negative integer/number.  Don't capture the either/or, just the entire outcome.
        let CSS_UNIT = "(?:" + CSS_NUMBER + ")|(?:" + CSS_INTEGER + ")";

        // Actual matching.
        // Parentheses and commas are optional, but not required.
        // Whitespace can take the place of commas or opening paren        
        let PERMISSIVE_MATCH3 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" +
            CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";
        let PERMISSIVE_MATCH4 = "[\\s|\\(]+(" + CSS_UNIT + ")[,|\\s]+(" +
            CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")[,|\\s]+(" + CSS_UNIT + ")\\s*\\)?";

        return {
            rgb: new RegExp("rgb" + PERMISSIVE_MATCH3),
            rgba: new RegExp("rgba" + PERMISSIVE_MATCH4),
            hsl: new RegExp("hsl" + PERMISSIVE_MATCH3),
            hsla: new RegExp("hsla" + PERMISSIVE_MATCH4),
            hsv: new RegExp("hsv" + PERMISSIVE_MATCH3),
            hsva: new RegExp("hsva" + PERMISSIVE_MATCH4),
            hex3: /^([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
            hex6: /^([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
            hex8: /^([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
        };
    })();

    // `stringInputToObject`
    // Permissive string parsing.  Take in a number of formats, and output an object
    // based on detected format.  Returns `{ r, g, b }` or `{ h, s, l }` or `{ h, s, v}`
    protected static stringInputToObject(color): any {
        let trimLeft = /^[\s,#]+/;
        let trimRight = /\s+$/;
        color = color.replace(trimLeft, "").replace(trimRight, "").toLowerCase();
        let named = false;
        if (Color.names[color]) {
            color = Color.names[color];
            named = true;
        } else if (color == "transparent") {
            return { r: 0, g: 0, b: 0, a: 0, format: "name" };
        }

        // Try to match string input using regular expressions.
        // Keep most of the number bounding out of this function - don't worry about [0,1] or [0,100] or [0,360]
        // Just return an object and let the conversion functions handle that.
        // This way the result will be the same whether the tinycolor is initialized with string or object.
        let match;
        // tslint:disable:no-conditional-assignment
        if ((match = Color.matchers.rgb.exec(color))) {
            return { r: match[1], g: match[2], b: match[3] };
        }
        if ((match = Color.matchers.rgba.exec(color))) {
            return { r: match[1], g: match[2], b: match[3], a: match[4] };
        }
        if ((match = Color.matchers.hsl.exec(color))) {
            return { h: match[1], s: match[2], l: match[3] };
        }
        if ((match = Color.matchers.hsla.exec(color))) {
            return { h: match[1], s: match[2], l: match[3], a: match[4] };
        }
        if ((match = Color.matchers.hsv.exec(color))) {
            return { h: match[1], s: match[2], v: match[3] };
        }
        if ((match = Color.matchers.hsva.exec(color))) {
            return { h: match[1], s: match[2], v: match[3], a: match[4] };
        }
        if ((match = Color.matchers.hex8.exec(color))) {
            return {
                a: Color.convertHexToDecimal(match[1]),
                r: Color.parseIntFromHex(match[2]),
                g: Color.parseIntFromHex(match[3]),
                b: Color.parseIntFromHex(match[4]),
                format: named ? "name" : "hex8"
            };
        }
        if ((match = Color.matchers.hex6.exec(color))) {
            return {
                r: Color.parseIntFromHex(match[1]),
                g: Color.parseIntFromHex(match[2]),
                b: Color.parseIntFromHex(match[3]),
                format: named ? "name" : "hex"
            };
        }
        if ((match = Color.matchers.hex3.exec(color))) {
            return {
                r: Color.parseIntFromHex(match[1] + "" + match[1]),
                g: Color.parseIntFromHex(match[2] + "" + match[2]),
                b: Color.parseIntFromHex(match[3] + "" + match[3]),
                format: named ? "name" : "hex"
            };
        }

        return false;
    }

    protected static validateWCAG2Parms(parms) {
        // return valid WCAG2 parms for isReadable.
        // If input parms are invalid, return {"level":"AA", "size":"small"}
        let level;
        let size;
        parms = parms || { level: "AA", size: "small" };
        level = (parms.level || "AA").toUpperCase();
        size = (parms.size || "small").toLowerCase();
        if (level !== "AA" && level !== "AAA") {
            level = "AA";
        }
        if (size !== "small" && size !== "large") {
            size = "small";
        }
        return { level, size };
    }
}
